import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RecipeCard from './RecipeCard';
import EmailModal from './EmailModal';
import './MocktailGenerator.css';

function MocktailGenerator({ userEmail }) {
  const [userInput, setUserInput] = useState('');
  const [generateImage, setGenerateImage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipesGenerated, setRecipesGenerated] = useState(0);
  const [hasEmail, setHasEmail] = useState(!!userEmail);

  // Load recipe count from localStorage on mount
  useEffect(() => {
    if (!userEmail) {
      const count = parseInt(localStorage.getItem('rysl_recipe_count') || '0', 10);
      setRecipesGenerated(count);
    } else {
      setHasEmail(true);
    }
  }, [userEmail]);


  // Big pool of creative mocktail prompts
  const allPrompts = [
    "Something tropical with coconut",
    "I have berries and basil",
    "Spicy margarita vibes, no alcohol",
    "Cozy fall drink, no fizz",
    "Refreshing and citrusy",
    "Something creamy and sweet",
    "Something with Island Vibes",
    "Warm winter mocktail",
    "Something easy to take to a party",
    "Coffee-based pick-me-up",
    "Fruity and frozen for poolside fun",
    "Herbal and calming",
    "I'd love a chocolate martini mocktail",
    "I have berries and blender - Hook me up",
    "Something with Seedlip and mint",
    "Something with berries that says, I'm not boring",
    "An after dinner mocktail",
    "Something upscale that says 'adulting' ",
    "I'd love a Paloma althernative",
    "How about a Nojito?"
  ];

  // Function to randomly select prompts
  const getRandomPrompts = (arr, count) => {
    const shuffled = [...arr].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Get 3 random prompts - only runs once when component mounts
  const [examplePrompts, setExamplePrompts] = useState(() => getRandomPrompts(allPrompts, 3));

  const handleGenerate = async () => {
    // Check if user has hit the limit (5 recipes) and no email
    if (!hasEmail && recipesGenerated >= 5) {
      // Fire Facebook Pixel InitiateCheckout event (high intent!)
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_name: 'Recipe Limit Reached',
          value: 27.00,
          currency: 'USD'
        });
      }
      setShowEmailModal(true);
      return;
    }

    if (!userInput.trim()) {
      setError('Please tell us what kind of mocktail you want!');
      return;
    }

    setIsGenerating(true);
    setError('');
    setRecipe(null);

    try {
      // This will call the backend API (to be implemented)
     const apiUrl = process.env.REACT_APP_API_URL || 'https://rysl-mixmaster-studio.onrender.com';
const response = await axios.post(`${apiUrl}/api/generate-mocktail`, {
        userInput,
        generateImage,
        userEmail: hasEmail ? userEmail : 'guest'
      });

      setRecipe(response.data);
      
      // Fire Facebook Pixel ViewContent event
      if (window.fbq) {
        window.fbq('track', 'ViewContent', {
          content_name: response.data.name,
          content_category: 'Mocktail Recipe',
          content_type: 'product'
        });
      }
      
      // Increment recipe count if no email
      if (!hasEmail) {
        const newCount = recipesGenerated + 1;
        setRecipesGenerated(newCount);
        localStorage.setItem('rysl_recipe_count', newCount.toString());
        
        // Show modal after recipe is generated (but not on the 6th attempt)
        setShowEmailModal(true);
      }
    } catch (err) {
      console.error('Error generating mocktail:', err);
      setError('Oops! Something went wrong. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
  // Your existing reset logic
  setRecipe(null);
  setUserInput('');
  setGenerateImage(false);
  setIsGenerating(false);
  setError('');
  
  // NEW: Refresh suggestions with 3 new random prompts
  setExamplePrompts(getRandomPrompts(allPrompts, 3));
  
  // NEW: Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  // NEW: Focus on input box
  setTimeout(() => {
    document.querySelector('textarea')?.focus();
  }, 300); // Small delay to ensure scroll completes first
};

  const handleEmailSubmit = async (email) => {
    // This will be called from the modal
    setHasEmail(true);
    setShowEmailModal(false);
    
    // Clear the recipe count from localStorage since they've signed up
    localStorage.removeItem('rysl_recipe_count');
    
    // Fire Facebook Pixel Lead event
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'MixMaster Email Signup',
        value: 27.00, // Your book/tripwire price
        currency: 'USD'
      });
    }
    
    // Send email to backend to save to Systeme.io
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://rysl-mixmaster-studio.onrender.com';
      await axios.post(`${apiUrl}/api/save-email`, {
        email: email
      });
      console.log('Email sent to backend successfully');
    } catch (error) {
      console.error('Error sending email to backend:', error);
      // Continue anyway - don't block the user experience
    }
    
    // Trigger parent component to save email to cookie
    if (window.handleEmailCapture) {
      window.handleEmailCapture(email);
    }
  };

  const handleModalClose = () => {
    setShowEmailModal(false);
  };


  return (
    <div className="mocktail-generator-container">
      {/* NEW: Centered logo at top */}
      <div className="logo-header">
        <img 
          src="/mixmaster_logo.png" 
          alt="RYSL MixMaster Studio" 
          className="header-logo-small"
        />
      </div>

      <div className="generator-header">
        <h1 className="generator-title">RYSL MIXMASTER STUDIO</h1>
      </div>

      {!recipe ? (
        <div className="input-section">
          <h2 className="input-title">What kind of mocktail are you craving?</h2>
          
          <textarea
            className="mocktail-input"
            placeholder="Tell me what you are in the mood for or what ingredients you have..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            rows={4}
          />

          <div className="example-prompts">
            <p className="example-label">💡 Try asking:</p>
            <div className="example-grid">
              {examplePrompts.map((prompt, index) => (
                <button
                  key={index}
                  className="example-button"
                  onClick={() => setUserInput(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          <div className="options-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={generateImage}
                onChange={(e) => setGenerateImage(e.target.checked)}
              />
              <span>Generate a custom image of this mocktail</span>
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* NEW: Prominent loading message */}
          {isGenerating && (
            <div className="loading-message-box">
              <div className="loading-spinner-large"></div>
              <p className="loading-text-main">
                {generateImage 
                  ? "Hold tight! Creating your custom mocktail image takes about 15-20 seconds... ✨" 
                  : "Mixing up your perfect mocktail recipe... 🍹"}
              </p>
              <p className="loading-text-sub">Your sassy mocktail is on its way! 💅</p>
            </div>
          )}

          <button
            className="generate-button"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="spinner-small"></span>
                Mixing your mocktail...
              </>
            ) : (
              <>🍹 Mix My Mocktail</>
            )}
          </button>
        </div>
      ) : (
        <RecipeCard 
          recipe={recipe} 
          onReset={handleReset} 
          userEmail={hasEmail ? userEmail : null}
          hasEmail={hasEmail}
          onEmailRequest={() => setShowEmailModal(true)}
        />
      )}
      
      {/* Email Modal */}
      {showEmailModal && (
        <EmailModal
          onEmailSubmit={handleEmailSubmit}
          onClose={handleModalClose}
          recipesRemaining={5 - recipesGenerated}
          isRequired={recipesGenerated >= 5}
        />
      )}
    </div>
  );
}

export default MocktailGenerator;
